/** What the Tracks page does to the list before it draws it.
 *
 * `GET /v1/tracks` answers every circuit the service knows about in one
 * reply — at most a hundred and twenty-odd rows, because that is how many
 * circuits the game has — so the filter box and the four status options are
 * arithmetic in the browser rather than a round trip per keystroke. The
 * endpoint does take `filter` and `status`, and a service with a thousand
 * circuits should use them; this one would spend a request to re-sort nine
 * cards.
 *
 * "Sorted by last activity" is the page's order and it is not the server's.
 * The query returns circuits by name, which is the right order for looking
 * one up, while the page is a feed of what people have been surveying.
 * Activity is the later of the last upload and the last publication, and a
 * circuit with neither sorts last rather than first: nothing has happened to
 * it, which is not news.
 *
 * Everything here is pure and takes its arguments, so the sorting and the
 * arithmetic can be tested by feeding it rows. The one figure that cannot be
 * derived honestly is called out where it is computed.
 */

import { relativeTime } from '@/lib/format';
import type { Track, TrackStats } from '@/lib/types';

/**
 * Circuits Gran Turismo 7 ships, which is the denominator in "N of 121
 * unsurveyed". It is a constant because the roster is not the service's to
 * know: nothing uploads a list of circuits nobody has driven, so the only
 * number the service can count is the ones that have been.
 */
export const GT7_CIRCUITS = 121;

/** More than the game has circuits, so one request is the whole map. The
 *  service caps a page at 200. */
export const TRACK_PAGE_SIZE = 200;

/** The four options on the toolbar. `surveying` is deliberately not one of
 *  them — a circuit with evidence and no publication is not a state anybody
 *  goes looking for, and it still appears under All. */
export type TrackFilter = 'all' | 'published' | 'pending' | 'held';

export const TRACK_FILTERS: ReadonlyArray<{ value: TrackFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'published', label: 'Published' },
  { value: 'pending', label: 'Pending' },
  { value: 'held', label: 'Held' },
];

export function isTrackFilter(value: string): value is TrackFilter {
  return TRACK_FILTERS.some((option) => option.value === value);
}

export function matchesFilter(track: Track, filter: TrackFilter): boolean {
  return filter === 'all' || track.status === filter;
}

/** Name, official id and slug all match, because people arrive holding any
 *  of the three: the name from the game, the id from a bundle they exported,
 *  the slug from a pull request they were linked to. */
export function matchesQuery(track: Track, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return (
    track.name.toLowerCase().includes(needle) ||
    track.official_id.toLowerCase().includes(needle) ||
    track.slug.toLowerCase().includes(needle)
  );
}

/** Epoch milliseconds of the last thing that happened to a circuit, or 0
 *  when nothing has. */
export function lastActivityAt(track: Track): number {
  let latest = 0;
  for (const iso of [track.last_upload_at, track.last_published]) {
    if (!iso) continue;
    const at = Date.parse(iso);
    if (Number.isFinite(at) && at > latest) latest = at;
  }
  return latest;
}

/** Newest first, then by name so the quiet end of the list has a stable
 *  order instead of shuffling with every reload. */
export function byLastActivity(a: Track, b: Track): number {
  const difference = lastActivityAt(b) - lastActivityAt(a);
  if (difference !== 0) return difference;
  return a.name.localeCompare(b.name);
}

/** The cards actually drawn: filtered, searched, and in the page's order.
 *  `filter` returns a fresh array, so sorting it in place touches nothing the
 *  caller still holds. */
export function visibleTracks(
  tracks: readonly Track[],
  query: string,
  filter: TrackFilter,
): Track[] {
  return tracks
    .filter((track) => matchesFilter(track, filter) && matchesQuery(track, query))
    .sort(byLastActivity);
}

/**
 * The four numbers in the header, from the list itself.
 *
 * `published` counts circuits with a publication date rather than circuits
 * whose status reads `published`, because a circuit that is on the map and
 * also has an upload waiting shows as `pending` — it is still published, and
 * the number people read as "how much of the map exists" should say so.
 *
 * `contributors` is the honest limit of what a list of per-circuit counts can
 * tell you: it adds up distinct accounts per circuit, so somebody who
 * surveyed three circuits is counted three times. It is an upper bound, not a
 * headcount. `GET /v1/tracks` should answer a `stats` block with the real
 * figure counted across the uploads table, and the page prefers that block
 * whenever the service sends one.
 */
export function deriveTrackStats(tracks: readonly Track[]): TrackStats {
  let published = 0;
  let pending = 0;
  let contributors = 0;

  for (const track of tracks) {
    if (track.last_published) published += 1;
    pending += track.pending;
    contributors += track.contributors;
  }

  return {
    published,
    pending,
    contributors,
    total_circuits: GT7_CIRCUITS,
    unsurveyed: Math.max(0, GT7_CIRCUITS - tracks.length),
  };
}

/** `PR #233`. The number is what a reader recognises and what fits in a card
 *  footer; the whole URL is the link's `href` and nobody needs to read it.
 *  Null when the circuit has never had a pull request opened for it. */
export function pullRequestLabel(url: string): string | null {
  const trimmed = url.trim();
  if (!trimmed) return null;
  const number = /\/pull\/(\d+)/.exec(trimmed)?.[1] ?? /(\d+)\s*$/.exec(trimmed)?.[1];
  return number ? `PR #${number}` : 'PR';
}

/** `published 2 h ago`, or the plain fact that it has not been. */
export function publicationLabel(track: Track, from: Date = new Date()): string {
  return track.last_published
    ? `published ${relativeTime(track.last_published, from)}`
    : 'not yet published';
}
